import { useCallback, useEffect } from 'react';

import { CircleMinusIcon, CirclePlusIcon } from 'lucide-react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from '@/components/atoms/Accordion/Accordion';
import CodeBlock from '@/components/atoms/CodeBlock/CodeBlock';
import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import TitleSubtitle from '@/components/atoms/TitleSubtitle/TitleSubtitle';
import { Button } from '@/components/ui/button';
import { URLInput } from '@/components/ui/custom/URLInput/URLInput';
import { useIntegrations } from '@/hooks/useIntegrations';
import usePrompt from '@/hooks/usePrompt';
import { setDirty, setExpanded } from '@/redux/integrations/actions';
import { selectAccordion, selectWebchatConnectSnippet } from '@/redux/integrations/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { AccordionFieldsetProps, WebWidgetIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { getDocsUrl } from '@/util/constants';
import { urlPattern } from '@/util/validation/patterns';

import styles from './ConnectAndSecure.module.scss';

type Form = { domains: { name: string }[] };

const ConnectAndSecure = ({
  type,
  integration,
  toggleAccordion,
  registerAccordion,
}: AccordionFieldsetProps<Form, WebWidgetIntegration>) => {
  const { t } = useTranslation();
  const { expanded } = useSelector(selectAccordion);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const codeSnippet = useSelector((state: RootState) =>
    selectWebchatConnectSnippet(state, integration?.integration_id)
  );
  const dispatch = useDispatch();
  const { updateIntegration, updateStatus } = useIntegrations<WebWidgetIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );

  const getDefaultValues = useCallback((domains: string[]) => {
    if (!domains) {
      return [{ name: '' }];
    }
    return domains.reduce(
      function (accumulator, domain) {
        const currentObject = { name: domain };
        accumulator.push(currentObject);
        return accumulator;
      },
      domains.length > 0 ? [] : [{ name: '' }]
    );
  }, []);

  const formMethods = useForm<Form>({ mode: 'onSubmit' });

  const {
    reset,
    formState: { errors, isDirty },
    control,
    handleSubmit,
    trigger,
  } = formMethods;

  const { fields, append, remove } = useFieldArray({ control, name: 'domains' });

  const values = useWatch({ control, name: 'domains' });

  useEffect(() => {
    reset({ domains: getDefaultValues(integration?.config?.domains) });
  }, [getDefaultValues, integration?.config?.domains, reset]);

  const handleCreate = useCallback(() => {
    if (canWrite) {
      append({ name: '' });
    }
  }, [append, canWrite]);

  const handleDelete = useCallback(
    (index) => {
      if (canWrite) {
        remove(index);
      }
    },
    [canWrite, remove]
  );

  const handleUpdateIntegration = useCallback(() => {
    const domains = formMethods
      .getValues()
      .domains.map((domain) => domain.name.trim())
      .filter((x) => x !== '');
    const uniqDomains = [...new Set(domains)];
    updateIntegration(
      {
        ...integration,
        config: {
          ...integration?.config,
          domains: uniqDomains.length > 0 ? uniqDomains : undefined,
        },
      },
      {
        onSuccess: () => {
          if (expanded === type) {
            dispatch(setExpanded(false));
          }
        },
      }
    );
  }, [dispatch, expanded, formMethods, integration, type, updateIntegration]);

  usePrompt(isDirty && canWrite, undefined, undefined, handleSubmit(handleUpdateIntegration));

  useEffect(() => {
    dispatch(setDirty(isDirty));
  }, [dispatch, isDirty]);

  useEffect(() => {
    if (expanded !== type && isDirty) {
      reset();
    }
  }, [expanded, isDirty, reset, type]);

  return (
    <>
      <Accordion
        title={t('integrations.web_connect.title')}
        subtitle={t('integrations.web_connect.subtitle')}
        footerText={t('integrations.web_connect.footer')}
        onSubmit={handleSubmit(handleUpdateIntegration)}
        disabled={!isDirty}
        isLoading={updateStatus === 'pending'}
        expanded={expanded === type}
        handleChange={toggleAccordion(type)}
        readOnly={!canWrite}
        ref={registerAccordion(type, handleSubmit(handleUpdateIntegration), trigger)}
      >
        <TitleSubtitle
          title={t('integrations.web_connect.title2')}
          subtitle={t('integrations.web_connect.2')}
        />

        <div className={styles.codeSnippet}>
          <div className={styles.codeBlockBontainer}>
            <CodeBlock>{codeSnippet}</CodeBlock>
            {codeSnippet && <CopyButton className={styles.copyCodeSnippet} data={codeSnippet} />}
          </div>
        </div>

        <ContextualHelp
          title={t('integrations.web_connect.help_title')}
          name="integration"
          links={[
            {
              label: t('integrations.web_connect.help_link'),
              url: getDocsUrl('/docs/integrations/web/web-getting-started#quick-start'),
            },
          ]}
        >
          {t('integrations.web_connect.help')}
        </ContextualHelp>

        <TitleSubtitle
          title={t('integrations.web_connect.domains_title')}
          subtitle={t('integrations.web_connect.domains_subtitle')}
        />

        <div>
          {fields.map((domain, index) => (
            <div className={styles.input} key={domain.id}>
              <Controller
                control={control}
                name={`domains.${index}.name`}
                rules={{ pattern: { value: urlPattern, message: t('validation.invalid_url') } }}
                render={({ field }) => (
                  <div className="w-96">
                    <URLInput {...field} readOnly={!canWrite} />
                    {errors?.domains?.[index]?.name?.message && (
                      <p className="mt-1 text-sm text-destructive">
                        {errors.domains[index].name.message}
                      </p>
                    )}
                  </div>
                )}
              />
              <Button
                variant="ghost-neutral"
                size="icon-sm"
                aria-label={t('common.create')}
                onClick={handleCreate}
                disabled={values[index]?.name === ''}
              >
                <CirclePlusIcon size={16} className="text-muted-foreground" />
              </Button>
              {fields.length > 1 && (
                <Button
                  variant="ghost-neutral"
                  size="icon-sm"
                  aria-label={t('common.delete')}
                  onClick={() => handleDelete(index)}
                >
                  <CircleMinusIcon size={16} className="text-muted-foreground" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </Accordion>
    </>
  );
};

export default ConnectAndSecure;
